import { useEffect, useRef, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  TextInput,
  NumberInput,
  Textarea,
  Button,
  Stack,
  Group,
  Select,
  Loader,
  Alert,
  MultiSelect,
  Divider,
  Badge,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCreateCEOEndorsement, useUpdateCEOEndorsement, useMeeting, useCEOEndorsement } from '../hooks';
import { AgendaItem } from '../../../api/bidCommittee';
import { IconAlertCircle } from '@tabler/icons-react';

interface EndorsementItem {
  agendaItemId: string;
  tenderQuotationNumber?: string;
  vendor: string;
  projectName: string;
  price: number;
  decision: string;
  detail?: string;
}

interface CEOEndorsementFormValues {
  meetingId: string;
  items: EndorsementItem[];
}

export function CEOEndorsementForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const meetingId = searchParams.get('meetingId') || '';
  const isEditMode = !!id;
  
  const { data: meeting, isLoading: meetingLoading } = useMeeting(meetingId || undefined);
  const { data: endorsement, isLoading: endorsementLoading } = useCEOEndorsement(id || '');
  const createEndorsement = useCreateCEOEndorsement();
  const updateEndorsement = useUpdateCEOEndorsement();
  
  const [selectedAgendaItemIds, setSelectedAgendaItemIds] = useState<string[]>([]);

  const form = useForm<CEOEndorsementFormValues>({
    initialValues: {
      meetingId: meetingId || '',
      items: [],
    },
    validate: {
      meetingId: (value) => (!value ? 'Meeting is required' : null),
      items: {
        vendor: (value) => (!value ? 'Vendor is required' : null),
        projectName: (value) => (!value ? 'Project name is required' : null),
        price: (value) => (value <= 0 ? 'Price must be greater than 0' : null),
        decision: (value) => (!value ? 'Decision is required' : null),
      },
    },
  });

  const formRef = useRef(form);
  formRef.current = form;

  // Sync Thaana fields before form re-renders
  const syncThaanaFieldsBeforeRender = () => {
    const containers = document.querySelectorAll('.thaana-input');
    containers.forEach((container) => {
      const input = container.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement;
      if (input) {
        const fieldName = input.getAttribute('name');
        if (fieldName) {
          const match = fieldName.match(/^items\.(\d+)\.(vendor|projectName|decision|detail)$/);
          if (match) {
            const index = parseInt(match[1], 10);
            const field = match[2] as keyof EndorsementItem;
            const currentValue = input.value;
            const formValue = formRef.current.values.items[index]?.[field];
            if (currentValue !== formValue) {
              const updatedItems = [...formRef.current.values.items];
              updatedItems[index] = { ...updatedItems[index], [field]: currentValue };
              formRef.current.setFieldValue('items', updatedItems);
            }
          }
        }
      }
    });
  };

  // Auto-resize textarea function
  const autoResizeTextarea = (textarea: HTMLTextAreaElement | null) => {
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  // Handle textarea input with auto-resize
  const handleTextareaChange = (index: number, field: 'decision' | 'detail', value: string) => {
    syncThaanaFieldsBeforeRender();
    const updatedItems = [...form.values.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    form.setFieldValue('items', updatedItems);
    
    setTimeout(() => {
      const textarea = document.querySelector(`textarea[name="items.${index}.${field}"]`) as HTMLTextAreaElement;
      if (textarea) {
        autoResizeTextarea(textarea);
      }
    }, 0);
  };

  // Initialize Thaana keyboard
  useEffect(() => {
    import('thaana-keyboard/ThaanaKeyboard.js').then(() => {
      if ((window as any).ThaanaKeyboard) {
        new (window as any).ThaanaKeyboard('.thaana-input');
        
        const syncThaanaField = (inputElement: HTMLInputElement | HTMLTextAreaElement) => {
          const fieldName = inputElement.getAttribute('name');
          if (!fieldName) return;
          
          const match = fieldName.match(/^items\.(\d+)\.(vendor|projectName|decision|detail)$/);
          if (!match) return;
          
          const index = parseInt(match[1], 10);
          const field = match[2] as keyof EndorsementItem;
          const currentForm = formRef.current;
          
          if (!currentForm || !currentForm.values.items[index]) return;
          
          const currentValue = inputElement.value;
          const formValue = currentForm.values.items[index][field];
          
          if (currentValue !== formValue) {
            const updatedItems = [...currentForm.values.items];
            updatedItems[index] = { ...updatedItems[index], [field]: currentValue };
            currentForm.setFieldValue('items', updatedItems);
          }
        };
        
        const findThaanaInputs = (): (HTMLInputElement | HTMLTextAreaElement)[] => {
          const inputs: (HTMLInputElement | HTMLTextAreaElement)[] = [];
          const containers = document.querySelectorAll('.thaana-input');
          
          containers.forEach((container) => {
            const input = container.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement;
            if (input) {
              inputs.push(input);
            }
          });
          
          return inputs;
        };
        
        const setupThaanaListeners = () => {
          const inputs = findThaanaInputs();
          inputs.forEach((input) => {
            const handler = () => syncThaanaField(input);
            ['input', 'change', 'blur', 'focus'].forEach(event => {
              input.addEventListener(event, handler);
            });
          });
        };
        
        setTimeout(setupThaanaListeners, 100);
        
        const observer = new MutationObserver(() => {
          setTimeout(setupThaanaListeners, 50);
        });
        
        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
        
        const syncAllThaanaFields = () => {
          const inputs = findThaanaInputs();
          inputs.forEach(input => {
            if (document.activeElement !== input) {
              syncThaanaField(input);
            }
          });
        };
        
        document.addEventListener('focusin', syncAllThaanaFields);
        document.addEventListener('click', syncAllThaanaFields);
        
        return () => {
          observer.disconnect();
          document.removeEventListener('focusin', syncAllThaanaFields);
          document.removeEventListener('click', syncAllThaanaFields);
        };
      }
    }).catch((err) => {
      console.error('Failed to load Thaana keyboard:', err);
    });
  }, []);

  // Load endorsement data in edit mode
  useEffect(() => {
    if (isEditMode && endorsement) {
      form.setValues({
        meetingId: endorsement.meetingId || '',
        items: endorsement.items.map((item) => ({
          agendaItemId: item.agendaItemId,
          tenderQuotationNumber: item.tenderQuotationNumber || '',
          vendor: item.vendor,
          projectName: item.projectName,
          price: Number(item.price),
          decision: item.decision,
          detail: item.detail || '',
        })),
      });
      setSelectedAgendaItemIds(endorsement.items.map((item) => item.agendaItemId));
    }
  }, [endorsement, isEditMode]);

  // Handle agenda item selection
  const handleAgendaItemSelection = (agendaItemIds: string[]) => {
    setSelectedAgendaItemIds(agendaItemIds);
    
    const currentItemIds = form.values.items.map((item) => item.agendaItemId);
    
    // Add new items
    const newIds = agendaItemIds.filter((id) => !currentItemIds.includes(id));
    const newItems = newIds.map((agendaItemId) => ({
      agendaItemId,
      tenderQuotationNumber: '',
      vendor: '',
      projectName: '',
      price: 0,
      decision: '',
      detail: '',
    }));
    
    // Remove deleted items
    const removedIds = currentItemIds.filter((id) => !agendaItemIds.includes(id));
    const updatedItems = form.values.items
      .filter((item) => !removedIds.includes(item.agendaItemId))
      .concat(newItems);
    
    form.setFieldValue('items', updatedItems);
  };

  const handleSubmit = (values: CEOEndorsementFormValues) => {
    if (values.items.length === 0) {
      form.setFieldError('items', 'At least one endorsement item is required');
      return;
    }

    const submitData = {
      meetingId: values.meetingId,
      items: values.items.map((item) => ({
        agendaItemId: item.agendaItemId,
        tenderQuotationNumber: item.tenderQuotationNumber || undefined,
        vendor: item.vendor,
        projectName: item.projectName,
        price: item.price,
        decision: item.decision,
        detail: item.detail || undefined,
      })),
    };

    if (isEditMode && id) {
      updateEndorsement.mutate(
        { id, data: submitData },
        {
          onSuccess: () => {
            navigate(`/bid-committee/endorsements/${id}`);
          },
        }
      );
    } else {
      createEndorsement.mutate(submitData, {
        onSuccess: () => {
          navigate(`/bid-committee/meetings/${values.meetingId}`);
        },
      });
    }
  };

  if (meetingLoading || endorsementLoading) {
    return (
      <Container size="lg" py="xl">
        <Loader />
      </Container>
    );
  }

  if (!meeting && !isEditMode) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          Meeting not found
        </Alert>
      </Container>
    );
  }

  const agendaItems = meeting?.agendaItems || [];
  const agendaItemOptions = agendaItems.map((item) => ({
    value: item.id,
    label: `${item.orderNo}. ${item.subject}`,
  }));

  return (
    <Container size="lg" py="xl">
      <Card>
        <Title order={2} mb="xl">
          {isEditMode ? 'Edit CEO Endorsement' : 'Create CEO Endorsement'}
        </Title>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            {!isEditMode && (
              <Select
                label="Meeting"
                data={meeting ? [{ value: meeting.id, label: `${meeting.meetingNumber} - ${meeting.title}` }] : []}
                value={form.values.meetingId}
                onChange={(value) => form.setFieldValue('meetingId', value || '')}
                disabled
                required
              />
            )}

            {isEditMode && meeting && (
              <div>
                <Text size="sm" c="dimmed">Meeting</Text>
                <Text fw={500}>{meeting.meetingNumber} - {meeting.title}</Text>
              </div>
            )}

            <MultiSelect
              label="Agenda Items"
              placeholder="Select agenda items"
              data={agendaItemOptions}
              value={selectedAgendaItemIds}
              onChange={handleAgendaItemSelection}
              required
              disabled={agendaItems.length === 0}
            />

            {agendaItems.length === 0 && (
              <Alert icon={<IconAlertCircle size={16} />} color="yellow">
                This meeting has no agenda items. Please add agenda items to the meeting first.
              </Alert>
            )}

            {form.values.items.length > 0 && (
              <>
                <Divider my="md" />
                <Title order={4}>Endorsement Items</Title>

                {form.values.items.map((item, index) => {
                  const agendaItem = agendaItems.find((ai) => ai.id === item.agendaItemId);
                  
                  return (
                    <Card key={item.agendaItemId} withBorder p="md">
                      <Stack gap="md">
                        <Group justify="space-between">
                          <Badge size="lg">
                            {agendaItem ? `${agendaItem.orderNo}. ${agendaItem.subject}` : 'Agenda Item'}
                          </Badge>
                        </Group>

                        <TextInput
                          label="Tender/Quotation Number (Optional)"
                          value={item.tenderQuotationNumber || ''}
                          onChange={(e) => {
                            const updatedItems = [...form.values.items];
                            updatedItems[index] = { ...updatedItems[index], tenderQuotationNumber: e.currentTarget.value };
                            form.setFieldValue('items', updatedItems);
                          }}
                        />

                        <div className="thaana-input" style={{ direction: 'rtl', textAlign: 'right' }}>
                          <TextInput
                            label="Vendor (Thaana)"
                            name={`items.${index}.vendor`}
                            value={item.vendor}
                            onChange={(e) => {
                              syncThaanaFieldsBeforeRender();
                              const updatedItems = [...form.values.items];
                              updatedItems[index] = { ...updatedItems[index], vendor: e.currentTarget.value };
                              form.setFieldValue('items', updatedItems);
                            }}
                            onBlur={() => syncThaanaFieldsBeforeRender()}
                            required
                            styles={{
                              input: {
                                direction: 'rtl',
                                textAlign: 'right',
                                fontFamily: 'Faruma, Arial, sans-serif',
                              },
                            }}
                          />
                        </div>

                        <div className="thaana-input" style={{ direction: 'rtl', textAlign: 'right' }}>
                          <TextInput
                            label="Project Name (Thaana)"
                            name={`items.${index}.projectName`}
                            value={item.projectName}
                            onChange={(e) => {
                              syncThaanaFieldsBeforeRender();
                              const updatedItems = [...form.values.items];
                              updatedItems[index] = { ...updatedItems[index], projectName: e.currentTarget.value };
                              form.setFieldValue('items', updatedItems);
                            }}
                            onBlur={() => syncThaanaFieldsBeforeRender()}
                            required
                            styles={{
                              input: {
                                direction: 'rtl',
                                textAlign: 'right',
                                fontFamily: 'Faruma, Arial, sans-serif',
                              },
                            }}
                          />
                        </div>

                        <NumberInput
                          label="Price"
                          value={item.price}
                          onChange={(value) => {
                            const updatedItems = [...form.values.items];
                            updatedItems[index] = { ...updatedItems[index], price: Number(value) || 0 };
                            form.setFieldValue('items', updatedItems);
                          }}
                          required
                          min={0}
                          decimalScale={2}
                          prefix="MVR "
                        />

                        <div className="thaana-input auto-resize-textarea" style={{ direction: 'rtl', textAlign: 'right' }}>
                          <Textarea
                            label="Decision (Thaana)"
                            name={`items.${index}.decision`}
                            value={item.decision}
                            onChange={(e) => handleTextareaChange(index, 'decision', e.currentTarget.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                setTimeout(() => {
                                  const textarea = e.currentTarget as HTMLTextAreaElement;
                                  if (textarea) {
                                    autoResizeTextarea(textarea);
                                  }
                                }, 0);
                              }
                            }}
                            onBlur={() => {
                              syncThaanaFieldsBeforeRender();
                              const textarea = document.querySelector(`textarea[name="items.${index}.decision"]`) as HTMLTextAreaElement;
                              if (textarea) autoResizeTextarea(textarea);
                            }}
                            required
                            minRows={3}
                            autosize
                            styles={{
                              input: {
                                direction: 'rtl',
                                textAlign: 'right',
                                fontFamily: 'Faruma, Arial, sans-serif',
                                resize: 'none',
                                overflow: 'hidden',
                              },
                            }}
                          />
                        </div>

                        <div className="thaana-input auto-resize-textarea" style={{ direction: 'rtl', textAlign: 'right' }}>
                          <Textarea
                            label="Detail (Thaana, Optional)"
                            name={`items.${index}.detail`}
                            value={item.detail || ''}
                            onChange={(e) => handleTextareaChange(index, 'detail', e.currentTarget.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                setTimeout(() => {
                                  const textarea = e.currentTarget as HTMLTextAreaElement;
                                  if (textarea) {
                                    autoResizeTextarea(textarea);
                                  }
                                }, 0);
                              }
                            }}
                            onBlur={() => {
                              syncThaanaFieldsBeforeRender();
                              const textarea = document.querySelector(`textarea[name="items.${index}.detail"]`) as HTMLTextAreaElement;
                              if (textarea) autoResizeTextarea(textarea);
                            }}
                            minRows={3}
                            autosize
                            styles={{
                              input: {
                                direction: 'rtl',
                                textAlign: 'right',
                                fontFamily: 'Faruma, Arial, sans-serif',
                                resize: 'none',
                                overflow: 'hidden',
                              },
                            }}
                          />
                        </div>
                      </Stack>
                    </Card>
                  );
                })}
              </>
            )}

            <Group justify="flex-end" mt="xl">
              <Button variant="outline" onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="submit" loading={createEndorsement.isPending || updateEndorsement.isPending}>
                {isEditMode ? 'Update Endorsement' : 'Create Endorsement'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Card>
    </Container>
  );
}





